import React, {useEffect, useState} from "react";
import {useHasRole} from "../../hooks/useRole";
import {
    RegistrationApiGet,
    usePostRegistrationMutation,
    usePutRegistrationByIdMutation,
    WorkFinishApiPut
} from "./reg_api";
import {FinishProps, RegFinishSchema} from "./RegEditSchema";
import {useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import Title from "../navigation/Title";
import RegStatus, {calculateTimeDifference} from "./RegStatus";
import QuantInput from "./QuantInput";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Check from "@mui/icons-material/Check";
import PunchClockOutlined from "@mui/icons-material/PunchClockOutlined";
import Refresh from "@mui/icons-material/Refresh";
import {StaticDateTimePicker} from "@mui/x-date-pickers";
import {FormikHelpers, useFormik} from "formik";
import {useDispatch} from "react-redux";
import {newError} from "../errors/errors";
import {supabase} from "../../supabase";
import dayjs from "dayjs";
import {dateFormat, datetimeFormat} from "../../lang/date-formats";

export default ({reg}: FinishProps) => {
    const canCloseWithTime = useHasRole(["Admin", "Foreman"])
    const [put, {error, isLoading: saving}] = usePutRegistrationByIdMutation()
    const [post, {error: err, isLoading}] = usePostRegistrationMutation()
    const [getTime, setGetTime] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const [action, setAction] = useState(1);

    const dispatch = useDispatch();
    useEffect(() => {
        if (error) {
            dispatch(newError(error))
        }
    }, [error]);
    useEffect(() => {
        if (err) {
            dispatch(newError(err))
        }
    }, [err]);

    const busy = saving || isLoading

    const initials: WorkFinishApiPut = {
        Quantity: 0,
        ScrapQuantity: 0,
        EndTime: null,
    }
    const onSubmit = async function (values: WorkFinishApiPut, formikHelpers: FormikHelpers<WorkFinishApiPut>) {

        if (!values.EndTime) {
            values.EndTime = new Date().toISOString()
        }
        const resp = await put({id: reg.id, body: {...values, Status: "RegFinished"}})

        if (!("error" in resp) && action == 2) {
            const {data, error} = await supabase.from("registration").insert({
                WorkerID: reg.WorkerID,
                MachineID: reg.MachineID,
                OperationID: reg.OperationID,
                Status: "RegNew",
            }).select().single();
            if (error) {
                dispatch(newError(error));
            } else {
                navigate(`/reg/${data?.id}`)
            }
        }

        if (!("error" in resp)) {
            navigate("/reg/")
        }
        formikHelpers.setSubmitting(false)
    }

    const {values, errors, isValid, submitForm, handleChange, handleSubmit, setFieldValue, handleBlur} = useFormik({
        initialValues: initials,
        onSubmit,
        validationSchema: RegFinishSchema
    })
    console.log(errors)
    return <Box component={"form"} onSubmit={handleSubmit}>
        <Title title={"Zaključek"}/>
        <Box sx={{mt: "1em"}}>
            <RegStatus reg={reg} flex={true}/>
        </Box>

        <Box sx={{
            "display": "grid",
            gridGap: "1em",
            gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}
        }}>

            <Card>
                <CardContent>
                    <Typography variant={"subtitle1"}>
                        Delovni nalog: {reg.workOrder?.number}
                    </Typography>

                    <Typography variant={"subtitle2"}></Typography>
                    <Typography variant={"h4"}>{reg.worker!.lastName + " " + reg.worker!.firstName}</Typography>
                    <QuantInput values={values} handleChange={handleChange} errors={errors}
                                handleBlur={handleBlur}/>

                </CardContent>
            </Card>
            {values.EndTime && <Card>
                <CardContent>
                    <Typography variant={"caption"}>Konec</Typography>
                    <Typography variant={"h3"}>{new Date(values.EndTime).toLocaleString("sl", datetimeFormat)}</Typography>
                    <Typography variant={"caption"}>Delo:</Typography>
                    <ShowHours reg={reg} values={values}/>
                </CardContent>
            </Card>}
        </Box>

        <Box sx={{display: "flex", gap: "1em", flexWrap: "wrap", mt: "1em"}}>
            {md && <Button
                variant={"contained"}
                size={"large"}
                title="Nazaj na registracijo"
                onClick={() => navigate("..")}>
                <ArrowBack/>
            </Button>}
            <Button
                disabled={!isValid || busy}
                color={"success"}
                variant={"contained"}
                size={"large"}
                type={"submit"}
                onClick={() => {
                    setAction(1)
                    submitForm();
                }}>
                {busy && action == 1 ? <CircularProgress size={"1.5em"}/> : <Check/>} &nbsp; Zaključi
            </Button>
            <Button
                disabled={!isValid || busy}
                color={"warning"}
                variant={"contained"}
                size={"large"}
                onClick={() => {
                    setAction(2)
                    setTimeout(() => {
                        submitForm();

                    }, 100)
                }}>
                {busy && action == 2 ? <CircularProgress size={"1.5em"}/> : <Refresh/>} &nbsp;  Novo delo
            </Button>
            {canCloseWithTime && <Button
                disabled={!isValid || busy}
                color={"warning"}
                variant={"contained"}
                size={"large"}
                onClick={async () => {
                    await setFieldValue("EndTime", new Date().toISOString())
                    setGetTime(true)
                }}><PunchClockOutlined/> Zaključi na čas</Button>}

        </Box>
        <Dialog open={getTime}>
            <DialogContent>
                <StaticDateTimePicker
                    displayStaticWrapperAs="mobile"
                    disableFuture={true}
                    openTo="hours"
                    ampm={false}
                    value={dayjs(values.EndTime)}
                    onChange={(newValue) => {
                        setFieldValue("EndTime", newValue?.format())
                    }}
                    onAccept={() => {
                        setGetTime(false)
                        submitForm()
                    }}
                    onClose={() => {
                        setGetTime(false)
                    }}
                />
            </DialogContent>
        </Dialog>
    </Box>
};

const ShowHours = ({reg, values}: { reg: RegistrationApiGet, values: WorkFinishApiPut }) => {
    const seconds = calculateTimeDifference(reg.StartTime, values.EndTime)
    const hours = Math.trunc(seconds / 3600)
    const minutes = Math.trunc((seconds - hours * 3600) / 60);
    return <Typography variant={"h5"}>{hours}:{minutes < 10 && 0}{minutes} [h:mm]</Typography>
}


